import React from "react";
import { Typography, Box } from "@mui/material";
import SimplePaper from "../../../components/SimplePaper";
import { MathComponent } from "mathjax-react";

const D16fromD1 = () => {
  const title = "Calculation of D16 (Shodasamsa) from D1 (Rasi)";

  const body = (
    <>
      <Typography variant="body1" paragraph>
        The D16 chart, or Shodasamsa chart, is used in Vedic astrology to
        analyze vehicles, material luxuries, and comforts. Below is the
        step-by-step process to calculate the D16 chart from the D1 (Rasi)
        chart.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Procedure to Calculate D16 (Shodasamsa) from D1 (Rasi)
      </Typography>

      <Typography variant="h5" gutterBottom>
        1. Identify the Planet's Degree in the D1 Chart
      </Typography>

      <Typography variant="body1" paragraph>
        First, identify the degree of the planet in the D1 chart. For example,
        if Jupiter is located at 10° Aries, this degree will be used for the
        calculation.
      </Typography>

      <Typography variant="h5" gutterBottom>
        2. Divide the Sign into Sixteen Parts
      </Typography>

      <Typography variant="body1" paragraph>
        Each zodiac sign is divided into sixteen equal parts, each spanning
        1.875°. To find the Shodasamsa position of a planet, we divide its
        degree in the sign by 1.875.
      </Typography>

      <Box sx={{ textAlign: "center", margin: "1rem 0" }}>
        <MathComponent
          tex={String.raw`Shodasamsa \ Position = \frac{Degree \ in \ Sign}{1.875}`}
        />
      </Box>

      <Typography variant="body1" paragraph>
        Depending on the result, we can determine the section in which the
        planet lies:
      </Typography>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`0 \leq \frac{Degree \ in \ Sign}{1.875} < 1`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>first 1.875°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`1 \leq \frac{Degree \ in \ Sign}{1.875} < 2`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>second 1.875°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`2 \leq \frac{Degree \ in \ Sign}{1.875} < 3`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>third 1.875°</strong>.
        </Typography>
      </Box>

      {/* Add similar blocks for all 16 parts */}

      <Typography variant="h5" gutterBottom>
        3. Assign the Shodasamsa Based on the Planet's Degree
      </Typography>

      <Typography variant="body1" paragraph>
        <strong>First 1.875°:</strong> The planet is assigned to the{" "}
        <strong>same sign</strong> as the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Second 1.875°:</strong> The planet is assigned to the{" "}
        <strong>2nd sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Third 1.875°:</strong> The planet is assigned to the{" "}
        <strong>3rd sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Fourth 1.875°:</strong> The planet is assigned to the{" "}
        <strong>4th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Fifth 1.875°:</strong> The planet is assigned to the{" "}
        <strong>5th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Sixth 1.875°:</strong> The planet is assigned to the{" "}
        <strong>6th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Seventh 1.875°:</strong> The planet is assigned to the{" "}
        <strong>7th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Eighth 1.875°:</strong> The planet is assigned to the{" "}
        <strong>8th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Ninth 1.875°:</strong> The planet is assigned to the{" "}
        <strong>9th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Tenth 1.875°:</strong> The planet is assigned to the{" "}
        <strong>10th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Eleventh 1.875°:</strong> The planet is assigned to the{" "}
        <strong>11th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Twelfth 1.875°:</strong> The planet is assigned to the{" "}
        <strong>12th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Thirteenth 1.875°:</strong> The planet is assigned to the{" "}
        <strong>13th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Fourteenth 1.875°:</strong> The planet is assigned to the{" "}
        <strong>14th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Fifteenth 1.875°:</strong> The planet is assigned to the{" "}
        <strong>15th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Sixteenth 1.875°:</strong> The planet is assigned to the{" "}
        <strong>16th sign</strong> from the D1 sign.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Example: Calculating Jupiter at 10° Aries
      </Typography>

      <Typography variant="body1" paragraph>
        Aries is the D1 sign. Jupiter at 10° falls in the sixth 1.875° (between
        9.375° and 11.25°). Therefore, it belongs to the{" "}
        <strong>6th sign</strong> from Aries, which is <strong>Virgo</strong>
      </Typography>

      <Typography variant="h4" gutterBottom>
        Python Program to Calculate D16 (Shodasamsa) from D1
      </Typography>

      <Typography variant="body1" paragraph>
        Below is a Python program that performs the calculation of the D16
        (Shodasamsa) position from the D1 (Rasi) chart, using the planet's
        degree and sign in the D1 chart.
      </Typography>

      <Box
        component="pre"
        sx={{
          padding: "1rem",
          backgroundColor: "#f5f5f5",
          borderRadius: "5px",
        }}
      >
        <code>
          {`def calculate_shodasamsa(degree, sign):
    # List of zodiac signs in order
    zodiac_signs = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces']

    # Get the index of the current sign in the zodiac cycle
    sign_index = zodiac_signs.index(sign)

    # Determine which Shodasamsa the planet is in based on its degree
    if degree <= 1.875:
        # First 1.875° belongs to the same sign
        shodasamsa_sign = zodiac_signs[sign_index]
    elif 1.875 < degree <= 3.75:
        # Second 1.875° belongs to the 2nd sign from the current sign
        shodasamsa_sign = zodiac_signs[(sign_index + 1) % 12]
    elif 3.75 < degree <= 5.625:
        # Third 1.875° belongs to the 3rd sign from the current sign
        shodasamsa_sign = zodiac_signs[(sign_index + 2) % 12]
    elif 5.625 < degree <= 7.5:
        # Fourth 1.875° belongs to the 4th sign from the current sign
        shodasamsa_sign = zodiac_signs[(sign_index + 3) % 12]
    elif 7.5 < degree <= 9.375:
        # Fifth 1.875° belongs to the 5th sign from the current sign
        shodasamsa_sign = zodiac_signs[(sign_index + 4) % 12]
    elif 9.375 < degree <= 11.25:
        # Sixth 1.875° belongs to the 6th sign from the current sign
        shodasamsa_sign = zodiac_signs[(sign_index + 5) % 12]
    elif 11.25 < degree <= 13.125:
        # Seventh 1.875° belongs to the 7th sign from the current sign
        shodasamsa_sign = zodiac_signs[(sign_index + 6) % 12]
    elif 13.125 < degree <= 15:
        # Eighth 1.875° belongs to the 8th sign from the current sign
        shodasamsa_sign = zodiac_signs[(sign_index + 7) % 12]
    elif 15 < degree <= 16.875:
        # Ninth 1.875° belongs to the 9th sign from the current sign
        shodasamsa_sign = zodiac_signs[(sign_index + 8) % 12]
    elif 16.875 < degree <= 18.75:
        # Tenth 1.875° belongs to the 10th sign from the current sign
        shodasamsa_sign = zodiac_signs[(sign_index + 9) % 12]
    elif 18.75 < degree <= 20.625:
        # Eleventh 1.875° belongs to the 11th sign from the current sign
        shodasamsa_sign = zodiac_signs[(sign_index + 10) % 12]
    elif 20.625 < degree <= 22.5:
        # Twelfth 1.875° belongs to the 12th sign from the current sign
        shodasamsa_sign = zodiac_signs[(sign_index + 11) % 12]
    elif 22.5 < degree <= 24.375:
        # Thirteenth 1.875° belongs to the 13th sign from the current sign
        shodasamsa_sign = zodiac_signs[(sign_index) % 12]
    elif 24.375 < degree <= 26.25:
        # Fourteenth 1.875° belongs to the 14th sign from the current sign
        shodasamsa_sign = zodiac_signs[(sign_index + 2) % 12]
    elif 26.25 < degree <= 28.125:
        # Fifteenth 1.875° belongs to the 15th sign from the current sign
        shodasamsa_sign = zodiac_signs[(sign_index + 3) % 12]
    else:
        # Sixteenth 1.875° belongs to the 16th sign from the current sign
        shodasamsa_sign = zodiac_signs[(sign_index + 4) % 12]

    return shodasamsa_sign

# Example usage
planet = "Jupiter"
degree = 10
sign = "Aries"

shodasamsa_sign = calculate_shodasamsa(degree, sign)
print(f"{planet} at {degree}° {sign} in the D1 chart is placed in {shodasamsa_sign} in the D16 chart.")`}
        </code>
      </Box>

      <Typography variant="body1" paragraph>
        The above Python program calculates the Shodasamsa for a planet based on
        its degree and sign in the D1 chart. The program divides the sign into
        sixteen equal parts and assigns the Shodasamsa based on the planet's
        degree.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Explanation of the Formula Used in the Python Program
      </Typography>

      <Typography variant="body1" paragraph>
        The program divides the sign into sixteen parts: the first 1.875°, the
        second 1.875°, and so on. It assigns the Shodasamsa based on the
        planet's degree within the sign:
        <br />
        - First 1.875° belongs to the same sign.
        <br />
        - Second 1.875° belongs to the 2nd sign from the D1 sign.
        <br />
        - Third 1.875° belongs to the 3rd sign from the D1 sign.
        <br />
        - Fourth 1.875° belongs to the 4th sign from the D1 sign.
        <br />
        - Fifth 1.875° belongs to the 5th sign from the D1 sign.
        <br />
        - Sixth 1.875° belongs to the 6th sign from the D1 sign.
        <br />
        - Seventh 1.875° belongs to the 7th sign from the D1 sign.
        <br />
        - Eighth 1.875° belongs to the 8th sign from the D1 sign.
        <br />
        - Ninth 1.875° belongs to the 9th sign from the D1 sign.
        <br />
        - Tenth 1.875° belongs to the 10th sign from the D1 sign.
        <br />
        - Eleventh 1.875° belongs to the 11th sign from the D1 sign.
        <br />
        - Twelfth 1.875° belongs to the 12th sign from the D1 sign.
        <br />
        - Thirteenth 1.875° belongs to the 13th sign from the D1 sign.
        <br />
        - Fourteenth 1.875° belongs to the 14th sign from the D1 sign.
        <br />
        - Fifteenth 1.875° belongs to the 15th sign from the D1 sign.
        <br />- Sixteenth 1.875° belongs to the 16th sign from the D1 sign.
      </Typography>

      <Typography variant="body1" paragraph>
        This method programmatically reproduces the manual calculation and can
        be easily adjusted for different planets, degrees, and signs.
      </Typography>
    </>
  );

  return <SimplePaper title={title} body={body} />;
};

export default D16fromD1;
